import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { ContentEditor } from '../../features/ContentEditor/ContentEditor.jsx';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import { buildEntityFormData, mapLocalizationsFromApi } from '../../shared/utils/buildEntityFormData.js';

const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'text', required: true },
  { name: 'shortDescription', label: 'Краткое описание', type: 'textarea' },
  { name: 'imgPath', label: 'Обложка', type: 'image', entityType: 'portfolio' },
  { name: 'content', label: 'Контент', type: 'content', hint: 'Пустой контент — на сайте покажется русская версия.' },
  { name: 'seoMetatags', label: 'SEO', type: 'seo' },
];

const emptyForm = {
  title: '',
  shortDescription: '',
  categoryId: 0,
  industryId: 0,
  imgPath: '',
  content: { items: [] },
  seoMetatags: { title: '', description: '', keywords: '' },
};

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Создание и редактирование кейса: основные поля, контент, SEO и локализации — одним запросом
 */
const PortfolioFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [localizations, setLocalizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [categoriesRes, industriesRes, portfolio] = await Promise.all([
          apiRequests.portfolio.getCategories(200),
          apiRequests.portfolio.getIndustries(200),
          isEdit ? apiRequests.portfolio.getById(id) : Promise.resolve(null),
        ]);
        const categoryItems = categoriesRes.items || [];
        const industryItems = industriesRes.items || [];
        setCategories(categoryItems);
        setIndustries(industryItems);

        if (portfolio) {
          setForm({
            id: portfolio.id,
            title: portfolio.title || '',
            shortDescription: portfolio.shortDescription || '',
            categoryId: portfolio.categoryId,
            industryId: portfolio.industryId,
            imgPath: portfolio.imgPath || '',
            content: { items: portfolio.content?.items || [] },
            seoMetatags: portfolio.seoMetatags || emptyForm.seoMetatags,
          });
          setLocalizations(mapLocalizationsFromApi(portfolio.localizations));
        } else {
          setForm({ ...emptyForm, categoryId: categoryItems[0]?.id || 0, industryId: industryItems[0]?.id || 0 });
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Не удалось загрузить данные кейса'));
      }
    };
    load();
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.title.trim()) {
      toast.error('Введите название кейса');
      return;
    }
    if (!isEdit && !mainImage) {
      toast.error('Загрузите обложку кейса');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const { imgPath, ...model } = form;
      const body = buildEntityFormData({ model, mainImage, localizations });

      if (isEdit) {
        await apiRequests.portfolio.update(body);
        toast.success('Кейс сохранён');
      } else {
        await apiRequests.portfolio.create(body);
        toast.success('Кейс создан');
        setTimeout(() => navigate('/portfolio'), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить кейс'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/portfolio')}
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Создать кейс'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control type="text" value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Например, Triggero" />
            </FormField>

            <FormField label="Краткое описание" hint="Показывается в карточке кейса и в начале страницы">
              <Form.Control as="textarea" rows={3} value={form.shortDescription} onChange={(e) => setField('shortDescription', e.target.value)} />
            </FormField>

            <Row>
              <Col md={6}>
                <FormField label="Категория">
                  <Form.Select value={form.categoryId} onChange={(e) => setField('categoryId', Number(e.target.value))}>
                    {categories.map((i) => (
                      <option key={i.id} value={i.id}>{i.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Индустрия">
                  <Form.Select value={form.industryId} onChange={(e) => setField('industryId', Number(e.target.value))}>
                    {industries.map((i) => (
                      <option key={i.id} value={i.id}>{i.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField label="Обложка" required={!isEdit}>
              <UploadPicture onSelect={setMainImage} initialValue={form.imgPath || undefined} entityType="portfolio" />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Контент страницы">
        <ContentEditor key={form.id || 'new'} value={form.content.items} onChange={(items) => setField('content', { items })} />
      </FormSection>

      <FormSection title="SEO">
        <SEOMetatagsForm seoMetatags={form.seoMetatags} onChange={(seo) => setField('seoMetatags', seo)} />
      </FormSection>

      <LocalizationsBlock value={localizations} onChange={setLocalizations} fields={LOCALIZATION_FIELDS} source={form} aiContext="кейс портфолио" />
    </FormLayout>
  );
};

export default PortfolioFormPage;
